import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AlertTriangle,
  CheckCircle,
  FileDown,
  XCircle,
} from 'lucide-react';
import type { SecurityProfile } from '@/types';

function SecurityProfileDisplay({ profile, appName }: { profile: SecurityProfile; appName: string }) {
  const theme = useTheme();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const module = await import('../../../../lib/exportSecurityProfile');
      await module.exportSecurityProfileExcel(profile, appName);
    } catch (err) {
      console.error('Failed to export Security Profile:', err);
    } finally {
      setExporting(false);
    }
  };

  const answerColor = (answer: string) => {
    switch (answer) {
      case 'Yes': return '#22c55e';
      case 'No': return '#ef4444';
      case 'Partial': return '#f59e0b';
      case 'N/A': return '#6b7280';
      default: return '#9ca3af';
    }
  };

  const confidenceColor = (conf: string) => {
    switch (conf) {
      case 'high': return '#22c55e';
      case 'medium': return '#f59e0b';
      case 'low': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const groupedAnswers = profile.answers.reduce((acc, ans) => {
    const cat = ans.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ans);
    return acc;
  }, {} as Record<string, typeof profile.answers>);

  const categoryOrder = [
    'General',
    'Authentication and Authorization',
    'Encryption',
    'Audit and Logging',
    'Host Protection and Patch Management',
    'Configuration',
    'Backup, Restore and Availability',
    'Input Validation',
  ];

  const sortedCategories = Object.keys(groupedAnswers).sort((a, b) => {
    const aIdx = categoryOrder.indexOf(a);
    const bIdx = categoryOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  return (
    <Stack spacing={2}>
      {/* Summary + Export Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            <strong>{profile.summary.total}</strong> questions |{' '}
            <Box component="span" sx={{ color: '#22c55e' }}><strong>{profile.summary.yesCount}</strong> Yes</Box> |{' '}
            <Box component="span" sx={{ color: '#ef4444' }}><strong>{profile.summary.noCount}</strong> No</Box> |{' '}
            <Box component="span" sx={{ color: '#f59e0b' }}><strong>{profile.summary.partialCount}</strong> Partial</Box> |{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}><strong>{profile.summary.naCount + profile.summary.unknownCount}</strong> N/A</Box>
          </Typography>
          {profile.summary.needsReview > 0 && (
            <Chip
              label={`${profile.summary.needsReview} need review`}
              size="small"
              sx={{ borderRadius: 5, fontSize: 11, height: 22, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', fontWeight: 600 }}
            />
          )}
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileDown size={14} />}
          onClick={handleExportExcel}
          disabled={exporting}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {exporting ? 'Exporting...' : 'Download Excel'}
        </Button>
      </Stack>

      {/* Version info */}
      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
        Generated: {new Date(profile.generatedAt).toLocaleString()} | Version: {profile.version}
      </Typography>

      {/* Grouped Questions */}
      {sortedCategories.map((category) => {
        const answers = groupedAnswers[category];
        const isExpanded = expandedCategories[category] !== false;
        const needsReviewCount = answers.filter(a => a.needsReview).length;

        return (
          <Paper key={category} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Box
              onClick={() => toggleCategory(category)}
              sx={{
                px: 2, py: 1.25, cursor: 'pointer',
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderBottom: isExpanded ? '1px solid' : 'none',
                borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{category}</Typography>
                <Chip label={answers.length} size="small" sx={{ borderRadius: 5, fontSize: 10, height: 18, minWidth: 24 }} />
                {needsReviewCount > 0 && (
                  <Chip
                    label={`${needsReviewCount} review`}
                    size="small"
                    sx={{ borderRadius: 5, fontSize: 10, height: 18, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
                  />
                )}
              </Stack>
              <Box sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <FileDown size={14} />
              </Box>
            </Box>

            {isExpanded && (
              <Stack spacing={0} divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
                {answers.sort((a, b) => a.questionNumber - b.questionNumber).map((ans) => (
                  <Box key={ans.questionId} sx={{ px: 2, py: 1.5, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.01) } }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 28, pt: 0.25 }}>Q{ans.questionNumber}</Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500, mb: 0.5 }}>{ans.question}</Typography>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1, py: 0.25, borderRadius: 1,
                            bgcolor: alpha(answerColor(ans.answer), 0.1),
                            color: answerColor(ans.answer),
                            fontSize: 11, fontWeight: 700,
                          }}>
                            {ans.answer === 'Yes' && <CheckCircle size={12} />}
                            {ans.answer === 'No' && <XCircle size={12} />}
                            {ans.answer === 'Partial' && <AlertTriangle size={12} />}
                            {ans.answer}
                          </Box>
                          <Typography sx={{ fontSize: 10.5, color: confidenceColor(ans.confidence) }}>
                            {ans.confidence} confidence
                          </Typography>
                          {ans.needsReview && (
                            <Chip
                              label="Needs Review"
                              size="small"
                              sx={{ borderRadius: 5, fontSize: 9, height: 16, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
                            />
                          )}
                        </Stack>
                        {ans.details && (
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>{ans.details}</Typography>
                        )}
                        {ans.evidence && ans.evidence.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                            {ans.evidence.slice(0, 5).map((ev, i) => (
                              <Chip
                                key={i}
                                label={ev}
                                size="small"
                                sx={{
                                  borderRadius: 1, fontSize: 10, height: 18,
                                  fontFamily: '"JetBrains Mono", monospace',
                                  bgcolor: alpha('#3b82f6', 0.06), color: '#3b82f6',
                                }}
                              />
                            ))}
                            {ans.evidence.length > 5 && (
                              <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>+{ans.evidence.length - 5} more</Typography>
                            )}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}

export default SecurityProfileDisplay;
